<template>
  <div class="col-6 card-wrapper">
    <div class="card mb-4">
      <!-- Card header -->
      <div class="card-header">
        <h3 class="mb-0">Agregar un nuevo artículo</h3>
      </div>
      <!-- Card body -->
      <div class="card-body">
        <!-- Form groups used in grid -->
        <form class="needs-validation" @submit.prevent="save">
          <div class="row">
            <div class="col-sm-12">
              <div class="form-group">
                <label class="control-label"> Código (*) </label>

                <el-input v-model="formData.code" v-validate="modelValidations.code" name="code" placeholder="Código">
                </el-input>

                <small v-show="code.invalid" class="text-danger">
                  {{ getError('code') }}
                </small>
              </div>
            </div>

            <div class="col-sm-12">
              <div class="form-group">
                <label class="control-label"> Descripción (*) </label>

                <el-input
                  v-model="formData.description"
                  v-validate="modelValidations.description"
                  name="description"
                  placeholder="Descripción"
                >
                </el-input>

                <small v-show="description.invalid" class="text-danger">
                  {{ getError('description') }}
                </small>
              </div>
            </div>

            <div class="col-sm-12">
              <div class="form-group">
                <label class="control-label"> Cantidad (*) </label>

                <el-input
                  v-model="formData.stock"
                  v-validate="modelValidations.stock"
                  name="stock"
                  placeholder="Cantidad"
                  type="number"
                >
                </el-input>

                <small v-if="article"> Cantidad de unidades en pedidos: {{ article.unitsInOrders }} </small>

                <small v-if="checkStock" class="text-danger"> Menor stock que pedidos (disponibilidad negativa) </small>

                <small v-show="stock.invalid" class="text-danger">
                  {{ getError('stock') }}
                </small>
              </div>
            </div>

            <div class="col-sm-12">
              <div class="form-group">
                <small v-if="article.ordersCount > 0" class="text-danger d-block">
                  El articulo posee pedidos ya realizados, por lo cual no se permite la edición los datos relacionados a
                  la campaña
                </small>
                <label class="control-label"> Campaña (*) </label>

                <el-select
                  v-model="formData.campaign"
                  v-validate="modelValidations.campaign"
                  :disabled="!editAllFields"
                  autocomplete="off"
                  filterable
                  name="campaign"
                  placeholder="Campaña"
                  @change="campaignUpdated"
                >
                  <el-option
                    v-for="option in campaignsOptions"
                    :key="option.value"
                    :label="option.label"
                    :value="option.value"
                  >
                  </el-option>
                </el-select>

                <small v-show="campaign.invalid" class="text-danger">
                  {{ getError('campaign') }}
                </small>
              </div>
            </div>

            <div class="col-sm-12">
              <div class="form-group">
                <label class="control-label"> Cultivo (*) </label>

                <el-select
                  v-model="formData.crop"
                  v-validate="modelValidations.crop"
                  :disabled="!formData.campaign || !editAllFields"
                  autocomplete="off"
                  filterable
                  name="crop"
                  placeholder="Cultivo"
                  @change="formData.variety = null"
                >
                  <el-option
                    v-for="option in cropsOptions"
                    :key="option.value"
                    :label="option.label"
                    :value="option.value"
                  >
                  </el-option>
                </el-select>

                <small v-show="crop.invalid" class="text-danger">
                  {{ getError('crop') }}
                </small>
              </div>
            </div>

            <div class="col-sm-12">
              <div class="form-group">
                <label class="control-label"> Variedad (*) </label>

                <el-select
                  v-model="formData.variety"
                  v-validate="modelValidations.variety"
                  :disabled="!formData.crop || !editAllFields"
                  autocomplete="off"
                  filterable
                  name="variety"
                  placeholder="Variedad"
                >
                  <el-option
                    v-for="option in varietiesOptions"
                    :key="option.value"
                    :label="option.label"
                    :value="option.value"
                  >
                  </el-option>
                </el-select>

                <small v-show="variety.invalid" class="text-danger">
                  {{ getError('variety') }}
                </small>
              </div>
            </div>

            <div class="col-md-12 text-right">
              <base-button native-type="submit" type="primary" :disabled="checkStock">Guardar</base-button>
            </div>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
import { Select, Option, DatePicker } from 'element-ui';
import { mapFields } from 'vee-validate';
import { mapState } from 'vuex';

export default {
  components: {
    [Select.name]: Select,
    [Option.name]: Option,
    [DatePicker.name]: DatePicker,
  },

  props: {
    editAllFields: {
      type: Boolean,
      default: true,
    },

    article: {
      type: Object,
      default: () => {
        return {
          code: null,
          description: null,
          variety: null,
          campaign: null,
          crop: null,
          stock: null,
        };
      },
    },
  },

  data() {
    return {
      crops: [],
      varieties: [],
      validated: false,

      formData: {
        code: this.article.code,
        description: this.article.description,
        variety: this.article.variety,
        campaign: this.article.campaign,
        crop: this.article.crop,
        stock: this.article.stock,
      },

      modelValidations: {
        code: {
          required: true,
          numeric: true,
        },

        description: {
          required: true,
        },

        crop: {
          required: true,
        },

        variety: {
          required: true,
        },

        campaign: {
          required: true,
        },

        stock: {
          required: true,
          numeric: true,
        },
      },
    };
  },

  computed: {
    ...mapState('models/campaign', ['campaigns']),

    ...mapFields(['code', 'description', 'variety', 'crop', 'campaign', 'stock']),

    checkStock() {
      return this.formData.stock < this.article.unitsInOrders;
    },

    cropsOptions() {
      let crops = [];
      if (this.formData.campaign && this.crops.length > 0) {
        crops = this.crops.filter((crop) =>
          crop.campaigns.map((campaign) => campaign.id).includes(this.formData.campaign)
        );
      }

      return this.$selectOptions(crops, { sort: true });
    },

    varietiesOptions() {
      let varieties = this.varieties;
      if (this.formData.crop && this.crops.length > 0) {
        varieties = varieties.filter((variety) => variety.cropId === this.formData.crop);
      }
      return this.$selectOptions(varieties, { sort: true });
    },

    campaignsOptions() {
      return this.$selectOptions(this.campaigns, { sort: true });
    },
  },

  created() {
    this.fetchVarieties();
    this.fetchCrops();
  },

  methods: {
    fetchCrops() {
      this.$services
        .resource('crops')
        .get({ include: 'campaigns' })
        .then((res) => res.data.data)
        .then((data) => (this.crops = data.crops));
    },

    fetchVarieties() {
      this.$services
        .resource('varieties')
        .get()
        .then((res) => res.data.data)
        .then((data) => (this.varieties = data.varieties));
    },

    campaignUpdated() {
      this.formData.crop = null;
      this.formData.variety = null;
    },

    async firstFormSubmit() {
      try {
        return await this.$validator.validateAll();
      } finally {
        this.validated = true;
      }
    },

    save() {
      this.firstFormSubmit().then((res) => {
        if (res) {
          this.$emit('save', this.formData);
        }
      });
    },

    getError(name) {
      return this.errors.first(name);
    },

    isValid(name) {
      return this.validated && !this.errors.has(name);
    },
  },
};
</script>

<style scoped>
.form-group .el-date-editor--date {
  width: 100%;
}
</style>
